import { createSelector } from 'reselect';

import {
  getLocalisationVars,
  getOriginEntityId,
  getViewId,
  getStay,
  getDestinationEntityId,
  getSelectedOutboundDate,
  getSelectedInboundDate,
} from '.';

import type { State, TripDuration } from './types';
import type { SkyscannerRadarSearchV1Search } from '@skyscanner-internal/radar-web-http-client';

type TripSegment = SkyscannerRadarSearchV1Search.TripSegment;
type TripCabinClass = SkyscannerRadarSearchV1Search.TripCabinClass;
type SearchRequest = SkyscannerRadarSearchV1Search.SearchRequest;

export const getDealHunterSessions = createSelector(
  (state: State) => state.dealHunter,
  (dealHunter) => dealHunter.sessions,
);

export const getDealHunterSessionForDuration = createSelector(
  (state: State) => state.dealHunter.sessions,
  (_: State, tripDuration: TripDuration | null) => tripDuration,
  (sessions, tripDuration) => (tripDuration ? sessions[tripDuration] : null),
);

const getPollingSessionTokenForDuration = createSelector(
  getDealHunterSessionForDuration,
  (sessionForDuration) => sessionForDuration?.pollingSessionToken,
);

const getRadarRequestContext = createSelector(
  getLocalisationVars,
  getViewId,
  ({ currency, locale, market }, viewId) => ({
    platform: 'PLATFORM_WEB' as const,
    supportedFeatures: ['SUPPORTED_FEATURE_FLIGHTS' as const],
    localisationContext: {
      market,
      locale,
      currencyCode: currency,
    },
    trustedFunnelId: viewId,
    viewId,
  }),
);

const formatDate = (date: Date) => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
});

const formatLocationsSpecifier = (entityId: string) => ({
  locations: {
    entityIds: [entityId],
  },
});

const CABIN_CLASS: TripCabinClass = 'CABIN_CLASS_ECONOMY';

export const getRadarCreateSessionRequest = createSelector(
  getRadarRequestContext,
  getStay,
  getDestinationEntityId,
  getOriginEntityId,
  getSelectedOutboundDate,
  getSelectedInboundDate,
  (
    requestContext,
    { adults, childrenAges },
    destinationEntityId,
    originEntityId,
    outboundDate,
    inboundDate,
  ): SearchRequest => {
    const tripSegments: TripSegment[] = [
      {
        origins: formatLocationsSpecifier(originEntityId),
        destinations: formatLocationsSpecifier(destinationEntityId),
        dates: {
          date: formatDate(outboundDate),
        },
      },
    ];

    if (inboundDate) {
      tripSegments.push({
        origins: formatLocationsSpecifier(destinationEntityId),
        destinations: formatLocationsSpecifier(originEntityId),
        dates: {
          date: formatDate(inboundDate),
        },
      });
    }

    return {
      requestContext,
      trip: {
        adults: adults ?? 1,
        childAges: childrenAges,
        cabinClass: CABIN_CLASS,
        tripSegments,
      },
    };
  },
);

export const getRadarPollRequest = createSelector(
  getRadarRequestContext,
  (_: State, tripDuration: TripDuration | null) =>
    getPollingSessionTokenForDuration(_, tripDuration),
  (_: State, tripDuration: TripDuration | null) => tripDuration,
  (requestContext, pollingSessionToken, tripDuration): SearchRequest | null => {
    if (!tripDuration) {
      return null;
    }
    if (!pollingSessionToken) {
      return null;
    }
    return {
      requestContext,
      pollingSessionToken,
    };
  },
);
